import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './marketing.css';

const SERVICES = [
  {
    icon: '🤖', color: '#4f8ef7',
    title: 'AI Chatbot Development',
    desc: 'Custom AI chatbots trained on your data. Handle customer support, lead qualification, internal knowledge bases, and sales — 24/7 without human intervention.',
    features: ['Trained on your docs, FAQs & products', 'Multi-channel: web, WhatsApp, Slack, email', 'Escalates complex issues to humans', 'Integrates with your CRM & helpdesk', 'Setup in 2–3 weeks'],
    result: '68% of support tickets resolved automatically',
    price: 'From $3,500/mo',
  },
  {
    icon: '⚡', color: '#7c3aed',
    title: 'Workflow Automation',
    desc: 'End-to-end business process automation. Connect your apps, eliminate manual data entry, and let AI handle repetitive tasks across your entire operation.',
    features: ['Invoice processing & approval workflows', 'HR onboarding & offboarding automation', 'CRM data sync & pipeline automation', 'Email & calendar automation', 'Custom n8n, Make, Zapier builds'],
    result: 'Save 40–200 hours per month per workflow',
    price: 'From $3,500/mo',
  },
  {
    icon: '📈', color: '#10b981',
    title: 'Algorithmic Trading Bot',
    desc: 'Custom algo trading systems with AI signal generation, automated execution, risk controls, and real-time P&L dashboards. Trade crypto, forex, and equities 24/7.',
    features: ['AI-powered signal generation', 'Multi-exchange execution (Binance, Alpaca, etc.)', 'Automated risk management & stop-loss', '10-year backtesting engine', 'Real-time P&L monitoring dashboard'],
    result: 'Trades 24/7 with zero emotion or fatigue',
    price: 'From $7,500/mo',
  },
  {
    icon: '📄', color: '#f59e0b',
    title: 'Invoice & Document Automation',
    desc: 'AI reads, extracts, matches and processes your invoices, contracts, and documents automatically. Zero manual data entry on clean documents.',
    features: ['PDF & email invoice extraction', 'PO matching & 3-way reconciliation', 'Auto-post to QuickBooks, Xero, SAP', 'Contract clause extraction & review', 'Fraud detection & anomaly flagging'],
    result: '99.2% accuracy — 40 hours/month saved',
    price: 'From $3,500/mo',
  },
  {
    icon: '👥', color: '#06b6d4',
    title: 'HR & Onboarding Automation',
    desc: 'Automate the entire employee lifecycle from offer letter to day-1 readiness. New hires are productive from their first day, every time.',
    features: ['Automated offer letters & contracts', 'IT provisioning & tool access setup', 'Slack workspace & calendar onboarding', '30/60/90-day check-in sequences', 'Offboarding & access revocation'],
    result: '12 hours saved per new hire',
    price: 'From $3,500/mo',
  },
  {
    icon: '🚀', color: '#a855f7',
    title: 'AI SaaS Product Development',
    desc: "Have an idea for an AI-powered software product? We build it from scratch — full-stack, production-ready, scalable. Your idea, our engineering.",
    features: ['Full-stack web & mobile apps', 'AI/LLM integration (GPT-4, Claude, Gemini)', 'API design & backend architecture', 'Database design & cloud deployment', 'Ongoing maintenance & iteration'],
    result: 'Launch your AI product in 6–10 weeks',
    price: 'From $15,000',
  },
  {
    icon: '📱', color: '#ec4899',
    title: 'AI Social Media Marketing',
    badge: '⚡ Powered by ViralVolt',
    desc: 'Dominate every social platform with AI-generated content. We automate your entire social media presence — from viral posts and captions to hashtag strategy, scheduling, and performance analytics.',
    features: ['AI viral caption & post generation', 'Platform-specific content: Instagram, TikTok, X, LinkedIn, Facebook', 'Smart hashtag engine for maximum reach', 'Brand voice training — sounds 100% like you', 'Content calendar automation & scheduling', 'Competitor & trend monitoring AI', 'Monthly performance & growth reports'],
    result: '10x faster content creation — go viral on autopilot',
    price: 'From $2,500/mo',
    viralvolt: true,
  },
];

export default function Services() {
  const navigate = useNavigate();

  return (
    <div className="mkt-root">
      <Helmet>
        <title>AI Automation Services | Gantima — Chatbots, Workflows, Trading Bots</title>
        <meta name="description" content="Explore Gantima's AI automation services: AI chatbot development, workflow automation, algorithmic trading bots, invoice automation, HR automation, and AI SaaS development. Starting from $3,500/month." />
        <meta name="keywords" content="AI chatbot development service, workflow automation service, algorithmic trading bot service, invoice automation, HR automation, AI SaaS development, business automation services, gantima services" />
        <link rel="canonical" href="https://www.gantima.com/services" />
        <meta property="og:title" content="AI Automation Services | Gantima" />
        <meta property="og:description" content="Custom AI chatbots, workflow automation, algo trading bots, invoice processing, HR automation and AI SaaS. Starting from $3,500/month." />
        <meta property="og:url" content="https://www.gantima.com/services" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "Gantima AI Automation Services",
          "url": "https://www.gantima.com/services",
          "itemListElement": SERVICES.map((s, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "name": s.title,
            "description": s.desc,
            "url": `https://www.gantima.com/services`
          }))
        })}</script>
      </Helmet>

      {/* Nav */}
      <nav className="mkt-nav">
        <a href="/" className="mkt-nav-brand" onClick={e => { e.preventDefault(); navigate('/'); }}>
          <img src="/GANTIMA-Logo.png" alt="Gantima" className="mkt-nav-logo-left-img" />
          <span className="mkt-nav-brand-tag">SOFTWARE AUTOMATION</span>
        </a>
        
        <ul className="mkt-nav-links">
          <li><a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Home</a></li>
          <li><a href="/services" onClick={e => { e.preventDefault(); navigate('/services'); }}>Services</a></li>
          <li><a href="/pricing" onClick={e => { e.preventDefault(); navigate('/pricing'); }}>Pricing</a></li>
          <li className="mkt-nav-dropdown">
            <span className="mkt-nav-dropdown-trigger">Products ▾</span>
            <div className="mkt-nav-dropdown-menu">
              <a href="https://www.viralvolt.ai/" target="_blank" rel="noopener noreferrer" className="mkt-nav-dropdown-featured">
                <div>
                  <div className="mkt-nav-dropdown-name">ViralVolt.ai</div>
                  <div className="mkt-nav-dropdown-desc">AI Social Media Marketing Tool</div>
                </div>
              </a>
              <div className="mkt-nav-dropdown-label">Solutions by Industry</div>
              <div className="mkt-nav-dropdown-grid">
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>E-Commerce</a>
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>HealthTech</a>
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>FinTech & Insurance</a>
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Algorithmic Trading</a>
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Crypto & DeFi</a>
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Legal & Compliance</a>
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Real Estate</a>
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>HR & Recruiting</a>
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Logistics & Supply Chain</a>
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>SaaS & Tech</a>
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Hedge Funds & Prop Trading</a>
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Forex & Futures</a>
                <a href="https://www.viralvolt.ai/" target="_blank" rel="noopener noreferrer" className="viralvolt-link">Social Media Marketing</a>
              </div>
            </div>
          </li>
              <li><a href="/about" onClick={e => { e.preventDefault(); navigate('/about'); }}>About</a></li>
          <li><a href="/careers" onClick={e => { e.preventDefault(); navigate('/careers'); }}>Careers</a></li>
        </ul>
        <div className="mkt-nav-actions">
          <button className="mkt-btn mkt-btn-primary mkt-btn-glow" onClick={() => navigate('/#consultation')}>Book Free Consultation</button>
        </div>
      </nav>

      {/* Hero */}
      <section style={{ padding: '130px 5% 80px', background: '#020817', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', width: '600px', height: '600px', borderRadius: '50%', background: 'rgba(79,142,247,0.08)', filter: 'blur(80px)', top: '-100px', right: '-100px', pointerEvents: 'none' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div className="mkt-pill" style={{ marginBottom: '20px' }}>OUR SERVICES</div>
          <h1 style={{ fontSize: 'clamp(2.2rem,4.5vw,3.6rem)', fontWeight: 900, color: '#f1f5f9', lineHeight: 1.1, marginBottom: '20px' }}>
            AI Automation Services<br /><span className="mkt-gradient-text">Built to Replace Manual Work</span>
          </h1>
          <p style={{ fontSize: '1.1rem', color: '#94a3b8', maxWidth: '580px', margin: '0 auto 36px', lineHeight: 1.75 }}>
            Every service is custom-built for your business. We don't sell templates — we engineer solutions that deliver measurable ROI within 60 days.
          </p>
          <div style={{ display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="mkt-btn mkt-btn-primary mkt-btn-lg mkt-btn-glow" onClick={() => navigate('/')}>Book Free Consultation</button>
            <button className="mkt-btn mkt-btn-ghost mkt-btn-lg" onClick={() => navigate('/pricing')}>View Pricing →</button>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="mkt-section" style={{ background: '#030d1d' }}>
        <div className="mkt-container">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px,1fr))', gap: '24px', maxWidth: '1200px', margin: '0 auto' }}>
            {SERVICES.map((s, i) => (
              <div key={i} className="mkt-service-card" style={{
                '--accent': s.color,
                ...(s.viralvolt ? { border: '1px solid rgba(236,72,153,0.3)', background: 'linear-gradient(135deg,rgba(236,72,153,0.04),rgba(124,58,237,0.04))' } : {})
              }}>
                <div className="mkt-service-accent-bar" />
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <div className="mkt-service-icon-wrap" style={{ marginBottom: 0 }}><span className="mkt-service-icon">{s.icon}</span></div>
                  {s.badge && (
                    <span style={{ fontSize: '.7rem', fontWeight: 700, color: '#a78bfa', background: 'rgba(124,58,237,0.15)', border: '1px solid rgba(124,58,237,0.3)', borderRadius: '999px', padding: '3px 10px', whiteSpace: 'nowrap' }}>
                      {s.badge}
                    </span>
                  )}
                </div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
                <ul style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: 0, margin: 0 }}>
                  {s.features.map((f, j) => (
                    <li key={j} style={{ fontSize: '.84rem', color: '#94a3b8', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                      <span style={{ color: '#10b981', fontWeight: 700, flexShrink: 0 }}>✓</span>{f}
                    </li>
                  ))}
                </ul>
                <div style={{ marginTop: 'auto', paddingTop: '16px', borderTop: '1px solid rgba(79,142,247,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '.8rem', color: '#10b981', fontWeight: 700 }}>📊 {s.result}</span>
                  <span style={{ fontSize: '.85rem', color: s.viralvolt ? '#ec4899' : '#4f8ef7', fontWeight: 700 }}>{s.price}</span>
                </div>
                {s.viralvolt && (
                  <a
                    href="https://www.viralvolt.ai/" target="_blank" rel="noopener noreferrer"
                    style={{ display:'block', marginTop: '14px', width: '100%', padding: '10px', background: 'linear-gradient(135deg,#ec4899,#7c3aed)', borderRadius: '10px', color: '#fff', fontWeight: 700, fontSize: '.85rem', cursor: 'pointer', letterSpacing: '.03em', textAlign:'center', textDecoration:'none' }}>
                    ⚡ See ViralVolt →
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mkt-cta-banner">
        <div className="mkt-cta-orb mkt-cta-orb-1" />
        <div className="mkt-cta-orb mkt-cta-orb-2" />
        <div className="mkt-cta-inner">
          <div className="mkt-cta-badge">🎯 FREE CONSULTATION</div>
          <h2>Not Sure Which Service You Need?</h2>
          <p>Book a free 30-minute strategy call. We'll map your workflow, identify the highest-ROI automations, and give you a custom plan — no commitment required.</p>
          <div className="mkt-cta-actions">
            <button className="mkt-btn mkt-btn-primary mkt-btn-xl mkt-btn-glow" onClick={() => navigate('/')}>Book Free Strategy Call</button>
            <span className="mkt-cta-note">No commitment · Free audit included · Results in 60 days</span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="mkt-footer">
        <div className="mkt-container">
          <div className="mkt-footer-grid">
            <div className="mkt-footer-brand">
              <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }} style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'14px', textDecoration:'none' }}>
                <img src="/GANTIMA-Logo.png" alt="Gantima" style={{ height:'48px', width:'auto', objectFit:'contain', filter:'drop-shadow(0 0 8px rgba(79,142,247,0.4))', flexShrink:0 }} />
                <span style={{ fontSize:'.55rem', fontWeight:800, color:'#4f8ef7', letterSpacing:'2.5px', textTransform:'uppercase', borderLeft:'1px solid rgba(79,142,247,0.25)', paddingLeft:'10px', lineHeight:1.5 }}>SOFTWARE<br/>AUTOMATION</span>
              </a>
              <p>AI automation agency helping businesses eliminate manual work and scale intelligently.</p>
              <a href="mailto:support@gantima.com" className="mkt-footer-email">support@gantima.com</a>
            </div>
            <div className="mkt-footer-col"><h4>Services</h4><ul>
              <li><a href="/services">AI Chatbot Development</a></li>
              <li><a href="/services">Workflow Automation</a></li>
              <li><a href="/services">Trading Bot</a></li>
              <li><a href="/services">Invoice Automation</a></li>
            </ul></div>
            <div className="mkt-footer-col"><h4>Company</h4><ul>
              <li><a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Home</a></li>
              <li><a href="https://www.viralvolt.ai/" target="_blank" rel="noopener noreferrer">ViralVolt.ai ⚡</a></li>
              <li><a href="/about" onClick={e => { e.preventDefault(); navigate('/about'); }}>About</a></li>
              <li><a href="/pricing" onClick={e => { e.preventDefault(); navigate('/pricing'); }}>Pricing</a></li>
              <li><a href="/careers" onClick={e => { e.preventDefault(); navigate('/careers'); }}>Careers</a></li>
            </ul></div>
            <div className="mkt-footer-col"><h4>Contact</h4><ul>
              <li><a href="mailto:support@gantima.com">support@gantima.com</a></li>
            </ul></div>
          </div>
          <div className="mkt-footer-bottom">
            <span>© 2026 Gantima Software. All rights reserved.</span>
            <a href="/privacy" onClick={e => { e.preventDefault(); navigate('/privacy'); }} style={{ color: '#64748b' }}>Privacy Policy</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
